(function (root, factory) {
  'use strict';

  var api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.KiiveStudioTools = root.KiiveStudioTools || {};
    root.KiiveStudioTools.tempo = api;
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var BPM_MIN = 1;
  var BPM_MAX = 999;
  var DEFAULT_BPM = 120;
  var DISPLAY_DECIMALS = 4;
  var SECONDS_DISPLAY_DECIMALS = 3;
  var HERTZ_DISPLAY_DECIMALS = 4;
  var SAMPLE_DISPLAY_DECIMALS = 2;
  var PITCH_CHANGE_MIN = -48;
  var PITCH_CHANGE_MAX = 48;
  var BPM_RESULT_DISPLAY_DECIMALS = 3;
  var SEMITONES_DISPLAY_DECIMALS = 4;
  var CENTS_DISPLAY_DECIMALS = 2;
  var PERCENT_DISPLAY_DECIMALS = 2;
  var METRIC_TICKS_PER_QUARTER = 96;
  var METRIC_MULTIPLIER_DISPLAY_DECIMALS = 6;
  var METRIC_DURATION_DISPLAY_DECIMALS = 3;
  var METRIC_VISUAL_MAX_INTERVALS = 16;

  var METRIC_RHYTHMS = Object.freeze([
    Object.freeze({ key: 'whole', label: 'Whole note', ticks: 384 }),
    Object.freeze({ key: 'dotted_half', label: 'Dotted half note', ticks: 288 }),
    Object.freeze({ key: 'half', label: 'Half note', ticks: 192 }),
    Object.freeze({ key: 'half_triplet', label: 'Half-note triplet', ticks: 128 }),
    Object.freeze({ key: 'dotted_quarter', label: 'Dotted quarter note', ticks: 144 }),
    Object.freeze({ key: 'quarter', label: 'Quarter note', ticks: 96 }),
    Object.freeze({ key: 'quarter_triplet', label: 'Quarter-note triplet', ticks: 64 }),
    Object.freeze({ key: 'dotted_eighth', label: 'Dotted eighth note', ticks: 72 }),
    Object.freeze({ key: 'eighth', label: 'Eighth note', ticks: 48 }),
    Object.freeze({ key: 'eighth_triplet', label: 'Eighth-note triplet', ticks: 32 }),
    Object.freeze({ key: 'dotted_sixteenth', label: 'Dotted sixteenth note', ticks: 36 }),
    Object.freeze({ key: 'sixteenth', label: 'Sixteenth note', ticks: 24 }),
    Object.freeze({ key: 'sixteenth_triplet', label: 'Sixteenth-note triplet', ticks: 16 }),
    Object.freeze({ key: 'dotted_thirty_second', label: 'Dotted 32nd note', ticks: 18 }),
    Object.freeze({ key: 'thirty_second', label: '32nd note', ticks: 12 }),
    Object.freeze({ key: 'thirty_second_triplet', label: '32nd-note triplet', ticks: 8 })
  ]);

  var SAMPLE_RATES_HZ = Object.freeze([
    44100,
    48000,
    88200,
    96000,
    176400,
    192000
  ]);

  var DIVISIONS = Object.freeze([
    Object.freeze({ key: 'whole', label: 'Whole', multiplier: 4 }),
    Object.freeze({ key: 'half', label: 'Half', multiplier: 2 }),
    Object.freeze({ key: 'quarter', label: 'Quarter', multiplier: 1 }),
    Object.freeze({ key: 'eighth', label: 'Eighth', multiplier: 0.5 }),
    Object.freeze({ key: 'sixteenth', label: 'Sixteenth', multiplier: 0.25 }),
    Object.freeze({ key: 'thirty_second', label: 'Thirty-second', multiplier: 0.125 })
  ]);

  var VARIANTS = Object.freeze([
    Object.freeze({ key: 'straight', label: 'Straight', multiplier: 1 }),
    Object.freeze({ key: 'dotted', label: 'Dotted', multiplier: 1.5 }),
    Object.freeze({ key: 'triplet', label: 'Triplet', multiplier: 2 / 3 })
  ]);

  var REVERB_PRE_DELAY_POINTS = Object.freeze([
    Object.freeze({ key: 'one_twenty_eighth', label: '1/128 note', quarterNoteBeats: 1 / 32 }),
    Object.freeze({ key: 'one_sixty_fourth', label: '1/64 note', quarterNoteBeats: 1 / 16 }),
    Object.freeze({ key: 'one_thirty_second', label: '1/32 note', quarterNoteBeats: 1 / 8 }),
    Object.freeze({ key: 'dotted_thirty_second', label: '1/32 dotted', quarterNoteBeats: 3 / 16 }),
    Object.freeze({ key: 'one_sixteenth', label: '1/16 note', quarterNoteBeats: 1 / 4 })
  ]);

  var REVERB_DECAY_POINTS = Object.freeze([
    Object.freeze({ key: 'half_beat', label: '1/2 beat', quarterNoteBeats: 0.5 }),
    Object.freeze({ key: 'one_beat', label: '1 beat', quarterNoteBeats: 1 }),
    Object.freeze({ key: 'two_beats', label: '2 beats', quarterNoteBeats: 2 }),
    Object.freeze({ key: 'one_bar', label: '1 bar in 4/4', quarterNoteBeats: 4 }),
    Object.freeze({ key: 'two_bars', label: '2 bars in 4/4', quarterNoteBeats: 8 })
  ]);

  function validationResult(valid, value, code, message) {
    return Object.freeze({
      valid: valid,
      value: value,
      code: code,
      message: message
    });
  }

  function validateBpm(input) {
    if (typeof input === 'string') {
      var normalized = input.trim();

      if (normalized === '') {
        return validationResult(false, null, 'empty', 'Enter a BPM value.');
      }

      if (!/^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(normalized)) {
        return validationResult(false, null, 'malformed', 'Enter a valid numeric BPM value.');
      }

      input = Number(normalized);
    }

    if (typeof input !== 'number' || !Number.isFinite(input)) {
      return validationResult(false, null, 'malformed', 'Enter a valid numeric BPM value.');
    }

    if (input < BPM_MIN || input > BPM_MAX) {
      return validationResult(false, null, 'range', 'Enter a BPM value from 1 to 999.');
    }

    return validationResult(true, input, null, '');
  }

  function validatePitchChange(input) {
    if (typeof input === 'string') {
      var normalized = input.trim();

      if (normalized === '') {
        return validationResult(false, null, 'empty', 'Enter a pitch change in semitones.');
      }

      if (!/^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(normalized)) {
        return validationResult(false, null, 'malformed', 'Enter a valid numeric pitch change.');
      }

      input = Number(normalized);
    }

    if (typeof input !== 'number' || !Number.isFinite(input)) {
      return validationResult(false, null, 'malformed', 'Enter a valid numeric pitch change.');
    }

    if (input < PITCH_CHANGE_MIN || input > PITCH_CHANGE_MAX) {
      return validationResult(false, null, 'range', 'Enter a pitch change from -48 to +48 semitones.');
    }

    return validationResult(true, Object.is(input, -0) ? 0 : input, null, '');
  }

  function validateMetricRhythm(input) {
    if (typeof input !== 'string') {
      return validationResult(false, null, 'malformed', 'Select a supported rhythmic value.');
    }

    var rhythm = METRIC_RHYTHMS.find(function (candidate) {
      return candidate.key === input;
    });

    if (!rhythm) {
      return validationResult(false, null, 'unsupported', 'Select a supported rhythmic value.');
    }

    return validationResult(true, rhythm, null, '');
  }

  function calculateDurations(bpm) {
    var validation = validateBpm(bpm);

    if (!validation.valid) {
      throw new RangeError(validation.message);
    }

    var quarterNote = 60000 / validation.value;
    var durations = {};

    DIVISIONS.forEach(function (division) {
      var straight = quarterNote * division.multiplier;
      var variants = {};

      VARIANTS.forEach(function (variant) {
        variants[variant.key] = straight * variant.multiplier;
      });

      durations[division.key] = Object.freeze(variants);
    });

    return Object.freeze(durations);
  }

  function calculateBeatMilliseconds(bpm, quarterNoteBeats) {
    var validation = validateBpm(bpm);

    if (!validation.valid) {
      throw new RangeError(validation.message);
    }

    if (typeof quarterNoteBeats !== 'number' || !Number.isFinite(quarterNoteBeats)) {
      throw new TypeError('A finite quarter-note beat value is required.');
    }

    if (quarterNoteBeats <= 0) {
      throw new RangeError('Quarter-note beats must be greater than zero.');
    }

    return (60000 / validation.value) * quarterNoteBeats;
  }

  function calculateFrequencies(bpm) {
    var durations = calculateDurations(bpm);
    var frequencies = {};

    DIVISIONS.forEach(function (division) {
      var variants = {};

      VARIANTS.forEach(function (variant) {
        variants[variant.key] = 1000 / durations[division.key][variant.key];
      });

      frequencies[division.key] = Object.freeze(variants);
    });

    return Object.freeze(frequencies);
  }

  function calculateSampleCounts(bpm, sampleRateHz) {
    if (
      typeof sampleRateHz !== 'number' ||
      !Number.isFinite(sampleRateHz) ||
      SAMPLE_RATES_HZ.indexOf(sampleRateHz) === -1
    ) {
      throw new RangeError('Select one of the six supported sample rates.');
    }

    var durations = calculateDurations(bpm);
    var sampleCounts = {};

    DIVISIONS.forEach(function (division) {
      var variants = {};

      VARIANTS.forEach(function (variant) {
        variants[variant.key] = (durations[division.key][variant.key] / 1000) * sampleRateHz;
      });

      sampleCounts[division.key] = Object.freeze(variants);
    });

    return Object.freeze(sampleCounts);
  }

  function calculateReverbTimings(bpm) {
    var preDelayMilliseconds = {};
    var decaySeconds = {};

    REVERB_PRE_DELAY_POINTS.forEach(function (point) {
      preDelayMilliseconds[point.key] = calculateBeatMilliseconds(bpm, point.quarterNoteBeats);
    });

    REVERB_DECAY_POINTS.forEach(function (point) {
      decaySeconds[point.key] = calculateBeatMilliseconds(bpm, point.quarterNoteBeats) / 1000;
    });

    return Object.freeze({
      preDelayMilliseconds: Object.freeze(preDelayMilliseconds),
      decaySeconds: Object.freeze(decaySeconds)
    });
  }

  function calculatePitchShift(originalBpm, targetBpm) {
    var originalValidation = validateBpm(originalBpm);
    var targetValidation = validateBpm(targetBpm);

    if (!originalValidation.valid) {
      throw new RangeError(originalValidation.message);
    }

    if (!targetValidation.valid) {
      throw new RangeError(targetValidation.message);
    }

    var speedRatio = targetValidation.value / originalValidation.value;
    var semitones = 12 * Math.log2(speedRatio);

    return Object.freeze({
      speedRatio: speedRatio,
      semitones: semitones,
      cents: 1200 * Math.log2(speedRatio),
      playbackSpeedPercent: speedRatio * 100,
      speedChangePercent: (speedRatio - 1) * 100
    });
  }

  function calculateTempoFromPitch(originalBpm, semitones) {
    var originalValidation = validateBpm(originalBpm);
    var pitchValidation = validatePitchChange(semitones);

    if (!originalValidation.valid) {
      throw new RangeError(originalValidation.message);
    }

    if (!pitchValidation.valid) {
      throw new RangeError(pitchValidation.message);
    }

    var speedRatio = Math.pow(2, pitchValidation.value / 12);

    return Object.freeze({
      speedRatio: speedRatio,
      resultingBpm: originalValidation.value * speedRatio,
      playbackSpeedPercent: speedRatio * 100,
      speedChangePercent: (speedRatio - 1) * 100
    });
  }

  function greatestCommonDivisor(first, second) {
    var a = Math.abs(first);
    var b = Math.abs(second);

    while (b !== 0) {
      var remainder = a % b;
      a = b;
      b = remainder;
    }

    return a;
  }

  function leastCommonMultiple(first, second) {
    return (first / greatestCommonDivisor(first, second)) * second;
  }

  function calculateMetricMarkerStride(intervalCount) {
    if (!Number.isInteger(intervalCount) || intervalCount < 1) {
      throw new RangeError('Metric interval count must be a positive integer.');
    }

    if (intervalCount <= METRIC_VISUAL_MAX_INTERVALS) {
      return 1;
    }

    for (var candidate = 2; candidate <= intervalCount; candidate += 1) {
      if (
        intervalCount % candidate === 0 &&
        intervalCount / candidate <= METRIC_VISUAL_MAX_INTERVALS
      ) {
        return candidate;
      }
    }

    return intervalCount;
  }

  function calculateMetricModulation(currentBpm, keepRhythmKey, countAsRhythmKey) {
    var bpmValidation = validateBpm(currentBpm);
    var keepValidation = validateMetricRhythm(keepRhythmKey);
    var countValidation = validateMetricRhythm(countAsRhythmKey);

    if (!bpmValidation.valid) {
      throw new RangeError(bpmValidation.message);
    }

    if (!keepValidation.valid) {
      throw new RangeError(keepValidation.message);
    }

    if (!countValidation.valid) {
      throw new RangeError(countValidation.message);
    }

    var keepRhythm = keepValidation.value;
    var countAsRhythm = countValidation.value;
    var multiplier = countAsRhythm.ticks / keepRhythm.ticks;
    var newBpm = bpmValidation.value * multiplier;
    var keptRhythmMilliseconds =
      (60000 / bpmValidation.value) * (keepRhythm.ticks / METRIC_TICKS_PER_QUARTER);
    var countAsMilliseconds =
      (60000 / newBpm) * (countAsRhythm.ticks / METRIC_TICKS_PER_QUARTER);
    var ratioDivisor = greatestCommonDivisor(countAsRhythm.ticks, keepRhythm.ticks);
    var currentPeriodScaled = METRIC_TICKS_PER_QUARTER * countAsRhythm.ticks;
    var keptPeriodScaled = keepRhythm.ticks * countAsRhythm.ticks;
    var newPeriodScaled = METRIC_TICKS_PER_QUARTER * keepRhythm.ticks;
    var cycleScaled = leastCommonMultiple(
      leastCommonMultiple(currentPeriodScaled, keptPeriodScaled),
      newPeriodScaled
    );
    var currentBeatCount = cycleScaled / currentPeriodScaled;
    var keptRhythmCount = cycleScaled / keptPeriodScaled;
    var newBeatCount = cycleScaled / newPeriodScaled;

    return Object.freeze({
      currentBpm: bpmValidation.value,
      keepRhythm: keepRhythm,
      countAsRhythm: countAsRhythm,
      newBpm: newBpm,
      multiplier: multiplier,
      keptRhythmMilliseconds: keptRhythmMilliseconds,
      countAsMilliseconds: countAsMilliseconds,
      ratio: Object.freeze({
        numerator: countAsRhythm.ticks / ratioDivisor,
        denominator: keepRhythm.ticks / ratioDivisor
      }),
      cycle: Object.freeze({
        scaledTicks: cycleScaled,
        currentBeatCount: currentBeatCount,
        keptRhythmCount: keptRhythmCount,
        newBeatCount: newBeatCount,
        currentMarkerStride: calculateMetricMarkerStride(currentBeatCount),
        keptMarkerStride: calculateMetricMarkerStride(keptRhythmCount),
        newMarkerStride: calculateMetricMarkerStride(newBeatCount)
      })
    });
  }

  function incrementDigits(digits) {
    var characters = digits.split('');

    for (var index = characters.length - 1; index >= 0; index -= 1) {
      if (characters[index] !== '9') {
        characters[index] = String(Number(characters[index]) + 1);
        return characters.join('');
      }

      characters[index] = '0';
    }

    return '1' + characters.join('');
  }

  function roundHalfUp(value, decimalPlaces) {
    var representation = Math.abs(value).toString().toLowerCase();
    var scientificParts = representation.split('e');
    var mantissa = scientificParts[0];
    var exponent = scientificParts.length === 2 ? Number(scientificParts[1]) : 0;
    var pointIndex = mantissa.indexOf('.');
    var integerLength = pointIndex === -1 ? mantissa.length : pointIndex;
    var digits = mantissa.replace('.', '');
    var decimalPosition = integerLength + exponent;
    var integerPart;
    var fractionPart;

    if (decimalPosition <= 0) {
      integerPart = '0';
      fractionPart = '0'.repeat(Math.abs(decimalPosition)) + digits;
    } else if (decimalPosition >= digits.length) {
      integerPart = digits + '0'.repeat(decimalPosition - digits.length);
      fractionPart = '';
    } else {
      integerPart = digits.slice(0, decimalPosition);
      fractionPart = digits.slice(decimalPosition);
    }

    integerPart = integerPart.replace(/^0+(?=\d)/, '');
    fractionPart = fractionPart + '0'.repeat(Math.max(0, decimalPlaces + 1 - fractionPart.length));

    var keptFraction = fractionPart.slice(0, decimalPlaces);
    var roundedDigits = integerPart + keptFraction;

    if (fractionPart.charAt(decimalPlaces) >= '5') {
      roundedDigits = incrementDigits(roundedDigits);
    }

    roundedDigits = roundedDigits.padStart(decimalPlaces + 1, '0');

    var copyValue;

    if (decimalPlaces === 0) {
      copyValue = roundedDigits;
    } else {
      var roundedPointIndex = roundedDigits.length - decimalPlaces;
      copyValue = roundedDigits.slice(0, roundedPointIndex) + '.' + roundedDigits.slice(roundedPointIndex);
      copyValue = copyValue.replace(/\.?0+$/, '');
    }

    if (copyValue !== '0' && value < 0) {
      copyValue = '-' + copyValue;
    }

    return copyValue;
  }

  function groupNumericString(value) {
    var sign = '';
    var unsignedValue = value;

    if (value.charAt(0) === '+' || value.charAt(0) === '-') {
      sign = value.charAt(0);
      unsignedValue = value.slice(1);
    }

    var parts = unsignedValue.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return sign + parts.join('.');
  }

  function formatM10Number(value, decimalPlaces, options) {
    if (typeof value !== 'number' || !Number.isFinite(value) || (!options.allowNegative && value < 0)) {
      throw new TypeError(options.typeMessage);
    }

    var copyValue = roundHalfUp(value, decimalPlaces);

    if (options.signed && copyValue !== '0' && value > 0) {
      copyValue = '+' + copyValue;
    }

    return Object.freeze({
      displayValue: groupNumericString(copyValue),
      copyValue: copyValue
    });
  }

  function formatResultBpm(value) {
    return formatM10Number(value, BPM_RESULT_DISPLAY_DECIMALS, {
      allowNegative: false,
      signed: false,
      typeMessage: 'A finite non-negative BPM result is required.'
    });
  }

  function formatSemitones(value) {
    return formatM10Number(value, SEMITONES_DISPLAY_DECIMALS, {
      allowNegative: true,
      signed: true,
      typeMessage: 'A finite semitone value is required.'
    });
  }

  function formatCents(value) {
    return formatM10Number(value, CENTS_DISPLAY_DECIMALS, {
      allowNegative: true,
      signed: true,
      typeMessage: 'A finite cents value is required.'
    });
  }

  function formatPlaybackSpeed(value) {
    var formatted = formatM10Number(value, PERCENT_DISPLAY_DECIMALS, {
      allowNegative: false,
      signed: false,
      typeMessage: 'A finite non-negative playback speed is required.'
    });

    return Object.freeze({
      displayValue: formatted.displayValue + '% of original',
      copyValue: formatted.copyValue
    });
  }

  function formatSpeedChange(value) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      throw new TypeError('A finite speed change is required.');
    }

    var copyValue = roundHalfUp(value, PERCENT_DISPLAY_DECIMALS);
    var numericValue = Number(copyValue);

    if (numericValue === 0) {
      return Object.freeze({
        numericValue: 0,
        direction: 'unchanged',
        displayValue: 'No change'
      });
    }

    if (numericValue > 0) {
      return Object.freeze({
        numericValue: numericValue,
        direction: 'faster',
        displayValue: '+' + groupNumericString(copyValue) + '% faster'
      });
    }

    return Object.freeze({
      numericValue: numericValue,
      direction: 'slower',
      displayValue: groupNumericString(copyValue.slice(1)) + '% slower'
    });
  }

  function formatMetricMultiplier(value) {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
      throw new TypeError('A finite positive metric-modulation multiplier is required.');
    }

    return roundHalfUp(value, METRIC_MULTIPLIER_DISPLAY_DECIMALS) + '×';
  }

  function formatMetricDuration(value) {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
      throw new TypeError('A finite positive kept-rhythm duration is required.');
    }

    return groupNumericString(roundHalfUp(value, METRIC_DURATION_DISPLAY_DECIMALS)) + ' ms';
  }

  function formatMilliseconds(value, maximumDecimals) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      throw new TypeError('A finite duration is required.');
    }

    var decimalPlaces = maximumDecimals === undefined ? DISPLAY_DECIMALS : maximumDecimals;

    if (!Number.isInteger(decimalPlaces) || decimalPlaces < 0 || decimalPlaces > 10) {
      throw new RangeError('Maximum decimals must be an integer from 0 to 10.');
    }

    var rounded = Number(value.toFixed(decimalPlaces));
    return Object.is(rounded, -0) ? '0' : String(rounded);
  }

  function formatSeconds(value, maximumDecimals) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      throw new TypeError('A finite duration is required.');
    }

    var decimalPlaces = maximumDecimals === undefined ? SECONDS_DISPLAY_DECIMALS : maximumDecimals;

    if (!Number.isInteger(decimalPlaces) || decimalPlaces < 0 || decimalPlaces > 10) {
      throw new RangeError('Maximum decimals must be an integer from 0 to 10.');
    }

    var rounded = Number(value.toFixed(decimalPlaces));
    return Object.is(rounded, -0) ? '0' : String(rounded);
  }

  function formatHertz(value, maximumDecimals) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      throw new TypeError('A finite frequency is required.');
    }

    var decimalPlaces = maximumDecimals === undefined ? HERTZ_DISPLAY_DECIMALS : maximumDecimals;

    if (!Number.isInteger(decimalPlaces) || decimalPlaces < 0 || decimalPlaces > 10) {
      throw new RangeError('Maximum decimals must be an integer from 0 to 10.');
    }

    var factor = Math.pow(10, decimalPlaces);
    var rounded = Math.round((value + Number.EPSILON) * factor) / factor;
    return Object.is(rounded, -0) ? '0' : String(rounded);
  }

  function formatSamples(value, maximumDecimals) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      throw new TypeError('A finite sample count is required.');
    }

    var decimalPlaces = maximumDecimals === undefined ? SAMPLE_DISPLAY_DECIMALS : maximumDecimals;

    if (!Number.isInteger(decimalPlaces) || decimalPlaces < 0 || decimalPlaces > 10) {
      throw new RangeError('Maximum decimals must be an integer from 0 to 10.');
    }

    var factor = Math.pow(10, decimalPlaces);
    var rounded = Math.round((value + Number.EPSILON) * factor) / factor;
    var copyValue = Object.is(rounded, -0) ? '0' : String(rounded);
    var parts = copyValue.split('.');

    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return Object.freeze({
      displayValue: parts.join('.'),
      copyValue: copyValue
    });
  }

  return Object.freeze({
    BPM_MIN: BPM_MIN,
    BPM_MAX: BPM_MAX,
    DEFAULT_BPM: DEFAULT_BPM,
    DISPLAY_DECIMALS: DISPLAY_DECIMALS,
    SECONDS_DISPLAY_DECIMALS: SECONDS_DISPLAY_DECIMALS,
    HERTZ_DISPLAY_DECIMALS: HERTZ_DISPLAY_DECIMALS,
    SAMPLE_DISPLAY_DECIMALS: SAMPLE_DISPLAY_DECIMALS,
    PITCH_CHANGE_MIN: PITCH_CHANGE_MIN,
    PITCH_CHANGE_MAX: PITCH_CHANGE_MAX,
    BPM_RESULT_DISPLAY_DECIMALS: BPM_RESULT_DISPLAY_DECIMALS,
    SEMITONES_DISPLAY_DECIMALS: SEMITONES_DISPLAY_DECIMALS,
    CENTS_DISPLAY_DECIMALS: CENTS_DISPLAY_DECIMALS,
    PERCENT_DISPLAY_DECIMALS: PERCENT_DISPLAY_DECIMALS,
    METRIC_TICKS_PER_QUARTER: METRIC_TICKS_PER_QUARTER,
    METRIC_MULTIPLIER_DISPLAY_DECIMALS: METRIC_MULTIPLIER_DISPLAY_DECIMALS,
    METRIC_DURATION_DISPLAY_DECIMALS: METRIC_DURATION_DISPLAY_DECIMALS,
    METRIC_VISUAL_MAX_INTERVALS: METRIC_VISUAL_MAX_INTERVALS,
    METRIC_RHYTHMS: METRIC_RHYTHMS,
    DIVISIONS: DIVISIONS,
    VARIANTS: VARIANTS,
    REVERB_PRE_DELAY_POINTS: REVERB_PRE_DELAY_POINTS,
    REVERB_DECAY_POINTS: REVERB_DECAY_POINTS,
    validateBpm: validateBpm,
    validatePitchChange: validatePitchChange,
    validateMetricRhythm: validateMetricRhythm,
    calculateDurations: calculateDurations,
    calculateFrequencies: calculateFrequencies,
    calculateSampleCounts: calculateSampleCounts,
    calculateBeatMilliseconds: calculateBeatMilliseconds,
    calculateReverbTimings: calculateReverbTimings,
    calculatePitchShift: calculatePitchShift,
    calculateTempoFromPitch: calculateTempoFromPitch,
    calculateMetricMarkerStride: calculateMetricMarkerStride,
    calculateMetricModulation: calculateMetricModulation,
    formatMilliseconds: formatMilliseconds,
    formatSeconds: formatSeconds,
    formatHertz: formatHertz,
    formatSamples: formatSamples,
    formatResultBpm: formatResultBpm,
    formatSemitones: formatSemitones,
    formatCents: formatCents,
    formatPlaybackSpeed: formatPlaybackSpeed,
    formatSpeedChange: formatSpeedChange,
    formatMetricMultiplier: formatMetricMultiplier,
    formatMetricDuration: formatMetricDuration
  });
});
